import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Wifi, Shield, Laptop, Phone } from 'lucide-react';
import './Home.css';

const Home = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="container">
            <h1 className="hero-title animated">Offgrid IT</h1>
            <p className="hero-subtitle animated">Independent Power. Connected Living.</p>
            <p className="hero-description animated">
              Reliable off-grid power, connectivity, security, and IT services for rural,
              remote, and underserved areas across South Africa.
            </p>
            <div className="hero-buttons animated">
              <Link to="/architecture-and-sustainability" className="btn-primary">
                Architecture & Sustainability
              </Link>
              <Link to="/it-services" className="btn-secondary">
                IT Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="services-section">
        <div className="container">
          <div className="section-header animated">
            <h2 className="heading-1">Our Services</h2>
            <p className="body-large">
              Comprehensive off-grid solutions tailored for South African communities
            </p>
          </div>

          <div className="services-grid">
            <div className="service-card animated">
              <div className="service-icon">
                <Zap size={32} />
              </div>
              <h3 className="service-title">Power-Independent IT Services</h3>
              <ul className="service-list">
                <li>Solar & Backup Power for IT</li>
                <li>Solar power systems</li>
                <li>UPS & inverter systems</li>
                <li>Load-shedding protection</li>
                <li>Power audits & optimization</li>
              </ul>
            </div>

            <div className="service-card animated">
              <div className="service-icon">
                <Wifi size={32} />
              </div>
              <h3 className="service-title">Off-Grid Internet & Connectivity</h3>
              <ul className="service-list">
                <li>Internet Solutions</li>
                <li>Long-range Wi-Fi</li>
                <li>Community Wi-Fi hotspots</li>
                <li>Farm & rural internet setups</li>
                <li>Network security (firewalls, VPNs)</li>
              </ul>
            </div>

            <div className="service-card animated">
              <div className="service-icon">
                <Shield size={32} />
              </div>
              <h3 className="service-title">Off-Grid Security & Surveillance</h3>
              <ul className="service-list">
                <li>Security & Alarm Systems</li>
                <li>Solar-powered CCTV systems</li>
                <li>Wireless camera installations</li>
                <li>Access control (biometric, RFID)</li>
                <li>Remote monitoring setups</li>
              </ul>
            </div>

            <div className="service-card animated">
              <div className="service-icon">
                <Laptop size={32} />
              </div>
              <h3 className="service-title">IT Support for Remote & Rural Areas</h3>
              <ul className="service-list">
                <li>On-site Technical Support</li>
                <li>Device setup (laptops/tablets/POS)</li>
                <li>Software installation & updates</li>
                <li>Virus removal & optimization</li>
                <li>Data backup & recovery</li>
              </ul>
            </div>

            <div className="service-card animated">
              <div className="service-icon">
                <Zap size={32} />
              </div>
              <h3 className="service-title">Smart Off-Grid Solutions</h3>
              <ul className="service-list">
                <li>Push To Talk Radio systems</li>
                <li>Smart irrigation systems</li>
                <li>Solar-powered IoT sensors</li>
                <li>Smart lighting & energy monitoring</li>
                <li>Remote farm monitoring</li>
              </ul>
            </div>

            <div className="service-card animated">
              <div className="service-icon">
                <Laptop size={32} />
              </div>
              <h3 className="service-title">Training & Capacity Building</h3>
              <ul className="service-list">
                <li>Training Services</li>
                <li>Basic computer literacy</li>
                <li>Solar system user training</li>
                <li>Network & CCTV training</li>
                <li>Cybersecurity awareness</li>
              </ul>
            </div>
          </div>

          <div className="services-cta animated">
            <Link to="/services" className="btn-primary">
              View All Services <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="container">
          <h2 className="heading-1 animated">Why Choose Offgrid IT?</h2>
          <div className="why-grid">
            <div className="why-card animated">
              <h3 className="heading-3">Designed for Load-Shedding</h3>
              <p className="body-medium">
                Our solutions are built to work seamlessly during power outages,
                keeping your operations running 24/7.
              </p>
            </div>
            <div className="why-card animated">
              <h3 className="heading-3">Works Where Others Don't</h3>
              <p className="body-medium">
                Specialized in rural and remote areas where traditional providers
                cannot reach.
              </p>
            </div>
            <div className="why-card animated">
              <h3 className="heading-3">Solar-Powered & Sustainable</h3>
              <p className="body-medium">
                Environmentally friendly, low-energy systems powered by South African sunshine.
              </p>
            </div>
            <div className="why-card animated">
              <h3 className="heading-3">Affordable for Communities</h3>
              <p className="body-medium">
                Cost-effective solutions designed for schools, clinics, farms, and NGOs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Markets We Serve Section */}
      <section className="markets-section">
        <div className="container">
          <h2 className="heading-1 animated">Markets We Serve</h2>
          <div className="markets-grid">
            <div className="market-card animated">
              <h3 className="heading-3">Our Clients</h3>
              <ul className="market-list">
                <li>20+ Lodges in Botswana & SA</li>
                <li>20+ Camps in Botswana & SA</li>
                <li>20+ game drive vehicles</li>
                <li>50+ private homes</li>
                <li>10+ farms</li>
                <li>10+ restaurants</li>
              </ul>
            </div>
            <div className="market-card animated">
              <h3 className="heading-3">Suitable for</h3>
              <ul className="market-list">
                <li>Lodges & eco-tourism</li>
                <li>Rural schools</li>
                <li>Clinics & health posts</li>
                <li>Farms & agribusinesses</li>
                <li>Churches & NGOs</li>
                <li>Cross border travellers</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content animated">
            <h2 className="heading-1">Ready to Go Off-Grid?</h2>
            <p className="body-large">
              Get a free site assessment & quote. Services available in South Africa,
              Botswana & Namibia.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary btn-cta">
                Request Free Assessment
              </Link>
              <a
                href="https://wa.me/27724603624"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary btn-whatsapp"
              >
                <Phone size={20} />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;